import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { toast } from "sonner";
import { 
  ArrowLeft, Save, ChevronRight, ChevronDown, Lock, Unlock, 
  AlertTriangle, CheckCircle2, Clock, DollarSign, User, MapPin,
  Stethoscope, Calendar, FileText, Activity, Send, ClipboardCheck,
  ChevronsUpDown, Check, X
} from "lucide-react";
import { format } from "date-fns";
import { cn } from "@/lib/utils";

import { API } from "@/config/api";
import { AREAS, REPS, getRepDisplayName, MEDICAL_AID_OPTIONS } from "@/config/constants";

const DELAY_CATEGORIES = ["Patient", "Admin", "Clinical", "External", "Other"];
const STUDY_VENUES = ["Home", "Hospital", "Clinic", "Lab", "Other"];
const REDO_CATEGORIES = ["Technical", "Patient", "Clinical", "Data Quality", "Other"];
const AUTH_METHODS = ["Phone", "Online Portal", "Fax", "Email", "Other"];

// Medical Aid Combobox Component
const MedicalAidCombobox = ({ value, onChange, disabled = false }) => {
  const [open, setOpen] = useState(false);
  
  return (
    <div className="space-y-1">
      <Label className="text-xs text-muted-foreground">Medical Aid</Label>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className={cn("w-full justify-between h-9 font-normal", disabled && "bg-muted cursor-not-allowed")}
            disabled={disabled}
            data-testid="medical-aid-combobox-detail"
          >
            <span className="truncate">{value || "Select medical aid..."}</span>
            <div className="flex items-center gap-1 shrink-0">
              {value && !disabled && (
                <X 
                  className="h-3 w-3 opacity-50 hover:opacity-100" 
                  onClick={(e) => {
                    e.stopPropagation();
                    onChange("");
                  }}
                />
              )}
              <ChevronsUpDown className="h-3 w-3 opacity-50" />
            </div>
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[280px] p-0" align="start">
          <Command>
            <CommandInput placeholder="Search medical aid..." />
            <CommandList>
              <CommandEmpty>No medical aid found.</CommandEmpty>
              <CommandGroup className="max-h-56 overflow-auto">
                {MEDICAL_AID_OPTIONS.map((aid) => (
                  <CommandItem
                    key={aid}
                    value={aid}
                    onSelect={() => {
                      onChange(aid);
                      setOpen(false);
                    }}
                  >
                    <Check
                      className={cn(
                        "mr-2 h-4 w-4",
                        value === aid ? "opacity-100" : "opacity-0"
                      )}
                    />
                    {aid}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
};

const PIPELINE_STAGES = [
  { key: "Referrals", label: "1. Referrals", icon: FileText },
  { key: "Pre SS", label: "2. Pre SS", icon: Clock },
  { key: "SS Authorization", label: "3. SS Auth", icon: ClipboardCheck },
  { key: "SS Facilitation", label: "4. SS Facilitation", icon: Stethoscope, commission: true },
  { key: "Post SS", label: "5. Post SS", icon: Activity },
  { key: "SS Reports", label: "6. SS Reports", icon: FileText },
  { key: "TT Facilitation", label: "7. TT Facilitation", icon: Stethoscope, commission: true },
  { key: "Post TT", label: "8. Post TT", icon: Activity },
  { key: "TT Sum", label: "9. TT Sum", icon: FileText },
  { key: "TT Report", label: "10. TT Report", icon: Send }
];

// Reusable form field component
const FormField = ({ label, value, onChange, type = "text", disabled = false, placeholder = "", options = null, formatOption = null }) => {
  if (options) {
    return (
      <div className="space-y-1">
        <Label className="text-xs text-muted-foreground">{label}</Label>
        <Select value={value || ""} onValueChange={onChange} disabled={disabled}>
          <SelectTrigger className={cn("h-9", disabled && "bg-muted")}>
            <SelectValue placeholder={placeholder || `Select ${label}`} />
          </SelectTrigger>
          <SelectContent>
            {options.map(opt => (
              <SelectItem key={opt} value={opt}>{formatOption ? formatOption(opt) : opt}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    );
  }
  
  return (
    <div className="space-y-1">
      <Label className="text-xs text-muted-foreground">{label}</Label>
      <Input
        type={type}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        placeholder={placeholder}
        className={cn("h-9", disabled && "bg-muted")}
      />
    </div>
  );
};

// Stage Section Component (collapsible)
const StageSection = ({ title, children, defaultOpen = true, badge = null }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  
  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen} className="border rounded-lg">
      <CollapsibleTrigger className="flex items-center justify-between w-full p-3 hover:bg-muted/50">
        <div className="flex items-center gap-2">
          {isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
          <span className="font-medium text-sm">{title}</span>
          {badge}
        </div>
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className="p-4 pt-0 space-y-4">
          {children}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
};

const CaseDetailPage = () => {
  const { tinNumber } = useParams();
  const navigate = useNavigate();
  
  const [caseData, setCaseData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState("stage_1");
  const [managerOverride, setManagerOverride] = useState(false);
  const [advanceDialogOpen, setAdvanceDialogOpen] = useState(false);
  
  // Stage data states
  const [stageData, setStageData] = useState({
    stage_1: {},
    stage_2: {},
    stage_3: {},
    stage_4: {},
    stage_5: {},
    stage_6: {},
    stage_7: {},
    stage_8: {},
    stage_9: {},
    stage_10: {}
  });

  const fetchCase = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/crm/cases/${tinNumber}`);
      setCaseData(response.data);
      
      // Initialize stage data from response
      const newStageData = {};
      for (let i = 1; i <= 10; i++) {
        newStageData[`stage_${i}`] = response.data[`stage_${i}`] || {};
      }
      setStageData(newStageData);
      
      // Set active tab to current stage
      const currentIdx = response.data.current_stage_index || 0;
      setActiveTab(`stage_${currentIdx + 1}`);
    } catch (error) {
      if (error.response?.status === 404) {
        toast.error("Case not found");
        navigate("/referrals");
      } else {
        toast.error("Failed to fetch case details");
      }
    } finally {
      setLoading(false);
    }
  }, [tinNumber, navigate]);

  useEffect(() => {
    fetchCase();
  }, [fetchCase]);

  const updateStageField = (stageNum, field, value) => {
    setStageData(prev => ({
      ...prev,
      [`stage_${stageNum}`]: {
        ...prev[`stage_${stageNum}`],
        [field]: value
      }
    }));
  };

  const saveStage = async (stageNum) => {
    setSaving(true);
    try {
      await axios.put(
        `${API}/crm/cases/${tinNumber}/stage/${stageNum}`,
        stageData[`stage_${stageNum}`]
      );
      toast.success(`Stage ${stageNum} saved`);
      await fetchCase();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save stage");
    } finally {
      setSaving(false);
    }
  };

  const advanceStage = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/crm/cases/${tinNumber}/advance`, {
        date: new Date().toISOString().split('T')[0]
      });
      toast.success("Case advanced to next stage");
      setAdvanceDialogOpen(false);
      await fetchCase();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to advance stage");
    } finally {
      setSaving(false);
    }
  };

  const isStageEditable = (stageNum) => {
    if (managerOverride) return true;
    if (!caseData) return false;
    const currentIdx = caseData.current_stage_index || 0;
    return stageNum === currentIdx + 1;
  };

  const isStageComplete = (stageNum) => {
    if (!caseData) return false;
    const currentIdx = caseData.current_stage_index || 0;
    return stageNum <= currentIdx;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!caseData) {
    return null;
  }

  const currentStageIdx = caseData.current_stage_index || 0;

  return (
    <div className="p-4 lg:p-6 space-y-3 sm:space-y-4 pb-6" data-testid="case-detail-page">
      {/* Header */}
      <div className="flex flex-col gap-3">
        <div className="flex items-start gap-2 sm:gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate("/referrals")} className="h-11 w-11 sm:h-9 sm:w-9 flex-shrink-0" data-testid="back-button">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="text-base sm:text-xl font-bold truncate" style={{ fontFamily: 'Manrope, sans-serif' }}>
                {caseData.name} {caseData.surname}
              </h1>
              {caseData.commission_eligible && (
                <Badge className="bg-emerald-100 text-emerald-800 text-xs">
                  <DollarSign className="w-3 h-3 mr-1" />
                  Commission
                </Badge>
              )}
            </div>
            <p className="text-xs sm:text-sm text-muted-foreground font-mono">TIN: {caseData.tin_number}</p>
          </div>
        </div>
        
        <div className="flex flex-wrap items-center gap-2">
          {/* Manager Override Toggle */}
          <div className="flex items-center gap-2 px-2 sm:px-3 py-1.5 border rounded-lg">
            <Switch
              checked={managerOverride}
              onCheckedChange={setManagerOverride}
              id="manager-override"
              data-testid="manager-override-toggle"
            />
            <Label htmlFor="manager-override" className="text-xs cursor-pointer">
              {managerOverride ? <Unlock className="w-4 h-4" /> : <Lock className="w-4 h-4" />}
            </Label>
          </div>
          
          {/* Advance Stage Button */}
          {currentStageIdx < 9 && (
            <Dialog open={advanceDialogOpen} onOpenChange={setAdvanceDialogOpen}>
              <DialogTrigger asChild>
                <Button className="gap-2 flex-1 sm:flex-initial h-10" data-testid="advance-stage-button">
                  <ChevronRight className="w-4 h-4" />
                  Advance Stage
                </Button>
              </DialogTrigger>
              <DialogContent className="mx-4 sm:mx-auto max-w-md">
                <DialogHeader>
                  <DialogTitle>Advance to Next Stage</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <p className="text-sm text-muted-foreground">
                    This will move the case from <strong>{PIPELINE_STAGES[currentStageIdx].key}</strong> to{" "}
                    <strong>{PIPELINE_STAGES[currentStageIdx + 1].key}</strong>.
                  </p>
                  {(currentStageIdx === 2 || currentStageIdx === 5) && (
                    <div className="p-3 bg-emerald-50 rounded-lg flex items-center gap-2">
                      <DollarSign className="w-4 h-4 text-emerald-600" />
                      <span className="text-sm text-emerald-800">
                        This stage triggers commission eligibility!
                      </span>
                    </div>
                  )}
                  <div className="flex flex-col sm:flex-row justify-end gap-2">
                    <Button variant="outline" onClick={() => setAdvanceDialogOpen(false)} className="h-11 sm:h-10">
                      Cancel
                    </Button>
                    <Button onClick={advanceStage} disabled={saving} className="h-11 sm:h-10">
                      {saving ? "Advancing..." : "Confirm Advance"}
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </div>

      {/* Case Summary Card */}
      <Card>
        <CardContent className="p-3 sm:p-4">
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 sm:gap-4">
            <div className="flex items-center gap-2">
              <User className="w-4 h-4 text-muted-foreground flex-shrink-0" />
              <div className="min-w-0">
                <p className="text-[10px] sm:text-xs text-muted-foreground">Rep</p>
                <p className="text-xs sm:text-sm font-medium truncate">{caseData.assigned_rep || "-"}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <MapPin className="w-4 h-4 text-muted-foreground flex-shrink-0" />
              <div className="min-w-0">
                <p className="text-[10px] sm:text-xs text-muted-foreground">Area</p>
                <p className="text-xs sm:text-sm font-medium truncate">{caseData.area}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Stethoscope className="w-4 h-4 text-muted-foreground flex-shrink-0" />
              <div className="min-w-0">
                <p className="text-[10px] sm:text-xs text-muted-foreground">Doctor</p>
                <p className="text-xs sm:text-sm font-medium truncate">{caseData.doctor}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <FileText className="w-4 h-4 text-muted-foreground flex-shrink-0" />
              <div className="min-w-0">
                <p className="text-[10px] sm:text-xs text-muted-foreground">Medical Aid</p>
                <p className="text-xs sm:text-sm font-medium truncate">{caseData.medical_aid || "-"}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Activity className="w-4 h-4 text-muted-foreground flex-shrink-0" />
              <div className="min-w-0">
                <p className="text-[10px] sm:text-xs text-muted-foreground">Current Stage</p>
                <Badge variant="secondary" className="text-[10px] sm:text-xs">{caseData.current_stage}</Badge>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-muted-foreground flex-shrink-0" />
              <div className="min-w-0">
                <p className="text-[10px] sm:text-xs text-muted-foreground">Referral Date</p>
                <p className="text-xs sm:text-sm font-medium truncate">
                  {caseData.created_at ? format(new Date(caseData.created_at), "MMM d, yyyy") : "-"}
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Stage Progress - Swipeable on mobile */}
      <Card>
        <CardContent className="p-2 sm:p-4">
          <div className="flex items-center gap-1 overflow-x-auto pb-2 -mx-2 px-2 sm:mx-0 sm:px-0">
            {PIPELINE_STAGES.map((stage, idx) => {
              const isComplete = idx < currentStageIdx;
              const isCurrent = idx === currentStageIdx;
              const StageIcon = stage.icon;
              
              return (
                <React.Fragment key={stage.key}>
                  <div
                    className={cn(
                      "flex items-center gap-0.5 sm:gap-1 px-1.5 sm:px-2 py-1 rounded-lg cursor-pointer transition-colors min-w-fit touch-manipulation active:scale-95",
                      isComplete && "bg-emerald-100 text-emerald-800",
                      isCurrent && "bg-primary/10 text-primary ring-2 ring-primary",
                      !isComplete && !isCurrent && "bg-muted text-muted-foreground"
                    )}
                    onClick={() => setActiveTab(`stage_${idx + 1}`)}
                  >
                    {isComplete ? (
                      <CheckCircle2 className="w-3 h-3 sm:w-4 sm:h-4" />
                    ) : (
                      <StageIcon className="w-3 h-3 sm:w-4 sm:h-4" />
                    )}
                    <span className="text-[10px] sm:text-xs font-medium whitespace-nowrap">{idx + 1}</span>
                    {stage.commission && (
                      <DollarSign className="w-2.5 h-2.5 sm:w-3 sm:h-3" />
                    )}
                  </div>
                  {idx < 9 && (
                    <div className={cn(
                      "w-2 sm:w-4 h-0.5 flex-shrink-0",
                      idx < currentStageIdx ? "bg-emerald-400" : "bg-muted"
                    )} />
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Stage Tabs - Horizontal scroll on mobile */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <div className="overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0">
          <TabsList className="w-max sm:w-full justify-start h-auto">
            {PIPELINE_STAGES.map((stage, idx) => {
              const isComplete = idx < currentStageIdx;
              const isCurrent = idx === currentStageIdx;
              
              return (
                <TabsTrigger
                  key={stage.key}
                  value={`stage_${idx + 1}`}
                  className={cn(
                    "text-[10px] sm:text-xs px-2 sm:px-3 py-1.5 sm:py-2 whitespace-nowrap",
                    isComplete && "text-emerald-700",
                    isCurrent && "text-primary font-bold"
                  )}
                >
                  <span className="hidden sm:inline">{stage.label}</span>
                  <span className="sm:hidden">{idx + 1}. {stage.key.split(' ')[0]}</span>
                  {isComplete && <CheckCircle2 className="w-2.5 h-2.5 sm:w-3 sm:h-3 ml-1" />}
                  {isCurrent && <span className="ml-1">●</span>}
                </TabsTrigger>
              );
            })}
          </TabsList>
        </div>

        {/* Stage 1: Referrals */}
        <TabsContent value="stage_1">
          <Card>
            <CardHeader className="p-3 sm:p-6">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-sm sm:text-lg">Stage 1: Referrals (Initial Intake)</CardTitle>
                  <CardDescription className="text-xs sm:text-sm">Patient referral information</CardDescription>
                </div>
                {!isStageEditable(1) && <Lock className="w-4 h-4 text-muted-foreground" />}
              </div>
            </CardHeader>
            <CardContent className="space-y-4 p-3 sm:p-6 pt-0">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                <FormField
                  label="Referral Date"
                  type="date"
                  value={stageData.stage_1.date}
                  onChange={(v) => updateStageField(1, "date", v)}
                  disabled={!isStageEditable(1)}
                />
                <FormField
                  label="Patient Name"
                  value={stageData.stage_1.name || caseData.name}
                  onChange={(v) => updateStageField(1, "name", v)}
                  disabled={!isStageEditable(1)}
                />
                <FormField
                  label="Surname"
                  value={stageData.stage_1.surname || caseData.surname}
                  onChange={(v) => updateStageField(1, "surname", v)}
                  disabled={!isStageEditable(1)}
                />
                <FormField
                  label="Doctor"
                  value={stageData.stage_1.doctor || caseData.doctor}
                  onChange={(v) => updateStageField(1, "doctor", v)}
                  disabled={!isStageEditable(1)}
                />
                <FormField
                  label="TIN Number"
                  value={stageData.stage_1.tin_number || caseData.tin_number}
                  onChange={(v) => updateStageField(1, "tin_number", v)}
                  disabled={true}
                />
                <MedicalAidCombobox
                  value={stageData.stage_1.medical_aid || caseData.medical_aid}
                  onChange={(v) => updateStageField(1, "medical_aid", v)}
                  disabled={!isStageEditable(1)}
                />
                <FormField
                  label="Area"
                  value={stageData.stage_1.area || caseData.area}
                  onChange={(v) => updateStageField(1, "area", v)}
                  disabled={!isStageEditable(1)}
                  options={AREAS}
                />
                <FormField
                  label="Assigned Rep"
                  value={stageData.stage_1.assigned_rep || caseData.assigned_rep}
                  onChange={(v) => updateStageField(1, "assigned_rep", v)}
                  disabled={!isStageEditable(1)}
                  options={REPS}
                  formatOption={getRepDisplayName}
                />
                <div className="flex items-center gap-3 pt-6">
                  <Switch
                    checked={stageData.stage_1.out_of_hospital || caseData.out_of_hospital}
                    onCheckedChange={(v) => updateStageField(1, "out_of_hospital", v)}
                    disabled={!isStageEditable(1)}
                  />
                  <Label className="text-sm">Out of Hospital</Label>
                </div>
              </div>
              {isStageEditable(1) && (
                <div className="flex justify-end pt-4">
                  <Button onClick={() => saveStage(1)} disabled={saving} className="gap-2">
                    <Save className="w-4 h-4" />
                    {saving ? "Saving..." : "Save Stage 1"}
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Stage 2: Pre SS (Large Process Stage) */}
        <TabsContent value="stage_2">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-lg">Stage 2: Pre SS</CardTitle>
                  <CardDescription>Pre-Sleep Study preparation and scheduling</CardDescription>
                </div>
                {!isStageEditable(2) && <Lock className="w-4 h-4 text-muted-foreground" />}
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <ScrollArea className="h-[600px] pr-4">
                {/* A. Load Patient Data */}
                <StageSection title="A. Load Patient Data">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <FormField
                      label="Uploaded Date"
                      type="date"
                      value={stageData.stage_2.load_uploaded_date}
                      onChange={(v) => updateStageField(2, "load_uploaded_date", v)}
                      disabled={!isStageEditable(2)}
                    />
                    <FormField
                      label="Lead Time (days)"
                      type="number"
                      value={stageData.stage_2.load_lead_time}
                      onChange={(v) => updateStageField(2, "load_lead_time", parseInt(v))}
                      disabled={!isStageEditable(2)}
                    />
                    <FormField
                      label="Delay Reason"
                      value={stageData.stage_2.load_delay_reason}
                      onChange={(v) => updateStageField(2, "load_delay_reason", v)}
                      disabled={!isStageEditable(2)}
                    />
                    <FormField
                      label="Delay Category"
                      value={stageData.stage_2.load_delay_category}
                      onChange={(v) => updateStageField(2, "load_delay_category", v)}
                      disabled={!isStageEditable(2)}
                      options={DELAY_CATEGORIES}
                    />
                  </div>
                </StageSection>

                {/* B. Contact Patient */}
                <StageSection title="B. Contact Patient" defaultOpen={false}>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <FormField
                      label="Contact Attempt 1"
                      type="date"
                      value={stageData.stage_2.contact_attempt_1}
                      onChange={(v) => updateStageField(2, "contact_attempt_1", v)}
                      disabled={!isStageEditable(2)}
                    />
                    <FormField
                      label="Contact Attempt 2"
                      type="date"
                      value={stageData.stage_2.contact_attempt_2}
                      onChange={(v) => updateStageField(2, "contact_attempt_2", v)}
                      disabled={!isStageEditable(2)}
                    />
                    <FormField
                      label="Contact Attempt 3"
                      type="date"
                      value={stageData.stage_2.contact_attempt_3}
                      onChange={(v) => updateStageField(2, "contact_attempt_3", v)}
                      disabled={!isStageEditable(2)}
                    />
                    <FormField
                      label="Contact Attempt 4"
                      type="date"
                      value={stageData.stage_2.contact_attempt_4}
                      onChange={(v) => updateStageField(2, "contact_attempt_4", v)}
                      disabled={!isStageEditable(2)}
                    />
                    <FormField
                      label="Lead Time (days)"
                      type="number"
                      value={stageData.stage_2.contact_lead_time}
                      onChange={(v) => updateStageField(2, "contact_lead_time", parseInt(v))}
                      disabled={!isStageEditable(2)}
                    />
                    <FormField
                      label="Delay Reason"
                      value={stageData.stage_2.contact_delay_reason}
                      onChange={(v) => updateStageField(2, "contact_delay_reason", v)}
                      disabled={!isStageEditable(2)}
                    />
                    <FormField
                      label="Delay Category"
                      value={stageData.stage_2.contact_delay_category}
                      onChange={(v) => updateStageField(2, "contact_delay_category", v)}
                      disabled={!isStageEditable(2)}
                      options={DELAY_CATEGORIES}
                    />
                  </div>
                </StageSection>

                {/* C. Schedule SS */}
                <StageSection title="C. Schedule Sleep Study" defaultOpen={false}>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    <FormField
                      label="Scheduled Date"
                      type="date"
                      value={stageData.stage_2.schedule_date}
                      onChange={(v) => updateStageField(2, "schedule_date", v)}
                      disabled={!isStageEditable(2)}
                    />
                    <FormField
                      label="Study Venue"
                      value={stageData.stage_2.schedule_study_venue}
                      onChange={(v) => updateStageField(2, "schedule_study_venue", v)}
                      disabled={!isStageEditable(2)}
                      options={STUDY_VENUES}
                    />
                    <FormField
                      label="Responsible"
                      value={stageData.stage_2.schedule_responsible}
                      onChange={(v) => updateStageField(2, "schedule_responsible", v)}
                      disabled={!isStageEditable(2)}
                    />
                    <FormField
                      label="Lead Time (days)"
                      type="number"
                      value={stageData.stage_2.schedule_lead_time}
                      onChange={(v) => updateStageField(2, "schedule_lead_time", parseInt(v))}
                      disabled={!isStageEditable(2)}
                    />
                    <FormField
                      label="Delay Reason"
                      value={stageData.stage_2.schedule_delay_reason}
                      onChange={(v) => updateStageField(2, "schedule_delay_reason", v)}
                      disabled={!isStageEditable(2)}
                    />
                  </div>
                </StageSection>

                {/* D. Reschedule */}
                <StageSection title="D. Reschedule" defaultOpen={false}>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <FormField
                      label="Reschedule Date"
                      type="date"
                      value={stageData.stage_2.reschedule_date}
                      onChange={(v) => updateStageField(2, "reschedule_date", v)}
                      disabled={!isStageEditable(2)}
                    />
                    <FormField
                      label="Venue"
                      value={stageData.stage_2.reschedule_venue}
                      onChange={(v) => updateStageField(2, "reschedule_venue", v)}
                      disabled={!isStageEditable(2)}
                      options={STUDY_VENUES}
                    />
                    <FormField
                      label="Reason"
                      value={stageData.stage_2.reschedule_reason}
                      onChange={(v) => updateStageField(2, "reschedule_reason", v)}
                      disabled={!isStageEditable(2)}
                    />
                  </div>
                </StageSection>

                {/* E. Cancellation */}
                <StageSection title="E. Cancellation" defaultOpen={false} badge={
                  stageData.stage_2.cancel_date && <Badge variant="destructive" className="ml-2">Canceled</Badge>
                }>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <FormField
                      label="Cancel Date"
                      type="date"
                      value={stageData.stage_2.cancel_date}
                      onChange={(v) => updateStageField(2, "cancel_date", v)}
                      disabled={!isStageEditable(2)}
                    />
                    <FormField
                      label="Reason"
                      value={stageData.stage_2.cancel_reason}
                      onChange={(v) => updateStageField(2, "cancel_reason", v)}
                      disabled={!isStageEditable(2)}
                    />
                    <FormField
                      label="Category"
                      value={stageData.stage_2.cancel_category}
                      onChange={(v) => updateStageField(2, "cancel_category", v)}
                      disabled={!isStageEditable(2)}
                      options={DELAY_CATEGORIES}
                    />
                  </div>
                </StageSection>

                {/* F. SS Facilitation Link */}
                <StageSection title="F. SS Facilitation Link" defaultOpen={false}>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <FormField
                      label="Facilitation Date"
                      type="date"
                      value={stageData.stage_2.facilitation_link_date}
                      onChange={(v) => updateStageField(2, "facilitation_link_date", v)}
                      disabled={!isStageEditable(2)}
                    />
                    <FormField
                      label="Facilitator"
                      value={stageData.stage_2.facilitation_link_facilitator}
                      onChange={(v) => updateStageField(2, "facilitation_link_facilitator", v)}
                      disabled={!isStageEditable(2)}
                    />
                  </div>
                </StageSection>

                {/* G. SS Cancellation */}
                <StageSection title="G. SS Cancellation" defaultOpen={false}>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <FormField
                      label="SS Cancel Date"
                      type="date"
                      value={stageData.stage_2.ss_cancel_date}
                      onChange={(v) => updateStageField(2, "ss_cancel_date", v)}
                      disabled={!isStageEditable(2)}
                    />
                    <FormField
                      label="Reason"
                      value={stageData.stage_2.ss_cancel_reason}
                      onChange={(v) => updateStageField(2, "ss_cancel_reason", v)}
                      disabled={!isStageEditable(2)}
                    />
                    <FormField
                      label="Category"
                      value={stageData.stage_2.ss_cancel_category}
                      onChange={(v) => updateStageField(2, "ss_cancel_category", v)}
                      disabled={!isStageEditable(2)}
                      options={DELAY_CATEGORIES}
                    />
                  </div>
                </StageSection>

                {/* H. Redo */}
                <StageSection title="H. Redo" defaultOpen={false}>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <FormField
                      label="Success Date"
                      type="date"
                      value={stageData.stage_2.redo_success_date}
                      onChange={(v) => updateStageField(2, "redo_success_date", v)}
                      disabled={!isStageEditable(2)}
                    />
                    <FormField
                      label="Redo Qty"
                      type="number"
                      value={stageData.stage_2.redo_qty}
                      onChange={(v) => updateStageField(2, "redo_qty", parseInt(v))}
                      disabled={!isStageEditable(2)}
                    />
                    <FormField
                      label="Reason"
                      value={stageData.stage_2.redo_reason}
                      onChange={(v) => updateStageField(2, "redo_reason", v)}
                      disabled={!isStageEditable(2)}
                    />
                    <FormField
                      label="Category"
                      value={stageData.stage_2.redo_category}
                      onChange={(v) => updateStageField(2, "redo_category", v)}
                      disabled={!isStageEditable(2)}
                      options={REDO_CATEGORIES}
                    />
                  </div>
                </StageSection>

                {/* I. Redo Cancellation */}
                <StageSection title="I. Redo Cancellation" defaultOpen={false}>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <FormField
                      label="Redo Cancel Date"
                      type="date"
                      value={stageData.stage_2.redo_cancel_date}
                      onChange={(v) => updateStageField(2, "redo_cancel_date", v)}
                      disabled={!isStageEditable(2)}
                    />
                    <FormField
                      label="Reason"
                      value={stageData.stage_2.redo_cancel_reason}
                      onChange={(v) => updateStageField(2, "redo_cancel_reason", v)}
                      disabled={!isStageEditable(2)}
                    />
                    <FormField
                      label="Category"
                      value={stageData.stage_2.redo_cancel_category}
                      onChange={(v) => updateStageField(2, "redo_cancel_category", v)}
                      disabled={!isStageEditable(2)}
                      options={DELAY_CATEGORIES}
                    />
                  </div>
                </StageSection>
              </ScrollArea>

              {isStageEditable(2) && (
                <div className="flex justify-end pt-4">
                  <Button onClick={() => saveStage(2)} disabled={saving} className="gap-2">
                    <Save className="w-4 h-4" />
                    {saving ? "Saving..." : "Save Stage 2"}
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Stage 3: SS Authorization */}
        <TabsContent value="stage_3">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-lg">Stage 3: SS Authorization</CardTitle>
                  <CardDescription>Sleep Study authorization and scheduling</CardDescription>
                </div>
                {!isStageEditable(3) && <Lock className="w-4 h-4 text-muted-foreground" />}
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <FormField
                  label="SS Service Date"
                  type="date"
                  value={stageData.stage_3.ss_service_date}
                  onChange={(v) => updateStageField(3, "ss_service_date", v)}
                  disabled={!isStageEditable(3)}
                />
                <FormField
                  label="Study Venue"
                  value={stageData.stage_3.study_venue}
                  onChange={(v) => updateStageField(3, "study_venue", v)}
                  disabled={!isStageEditable(3)}
                  options={STUDY_VENUES}
                />
                <FormField
                  label="Rescheduled Date"
                  type="date"
                  value={stageData.stage_3.rescheduled_date}
                  onChange={(v) => updateStageField(3, "rescheduled_date", v)}
                  disabled={!isStageEditable(3)}
                />
                <FormField
                  label="Rescheduled Venue"
                  value={stageData.stage_3.rescheduled_venue}
                  onChange={(v) => updateStageField(3, "rescheduled_venue", v)}
                  disabled={!isStageEditable(3)}
                  options={STUDY_VENUES}
                />
                <FormField
                  label="Authorization Date"
                  type="date"
                  value={stageData.stage_3.auth_date}
                  onChange={(v) => updateStageField(3, "auth_date", v)}
                  disabled={!isStageEditable(3)}
                />
                <FormField
                  label="Auth Method"
                  value={stageData.stage_3.auth_method}
                  onChange={(v) => updateStageField(3, "auth_method", v)}
                  disabled={!isStageEditable(3)}
                  options={AUTH_METHODS}
                />
                <FormField
                  label="Reference No"
                  value={stageData.stage_3.reference_no}
                  onChange={(v) => updateStageField(3, "reference_no", v)}
                  disabled={!isStageEditable(3)}
                />
              </div>
              {isStageEditable(3) && (
                <div className="flex justify-end pt-4">
                  <Button onClick={() => saveStage(3)} disabled={saving} className="gap-2">
                    <Save className="w-4 h-4" />
                    {saving ? "Saving..." : "Save Stage 3"}
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Stage 4: SS Facilitation (Commission Trigger) */}
        <TabsContent value="stage_4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-lg flex items-center gap-2">
                    Stage 4: SS Facilitation
                    <Badge className="bg-emerald-100 text-emerald-800">
                      <DollarSign className="w-3 h-3 mr-1" />
                      Commission Trigger
                    </Badge>
                  </CardTitle>
                  <CardDescription>Sleep Study execution - triggers commission eligibility</CardDescription>
                </div>
                {!isStageEditable(4) && <Lock className="w-4 h-4 text-muted-foreground" />}
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <StageSection title="Facilitation">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormField
                    label="Facilitation Date"
                    type="date"
                    value={stageData.stage_4.facilitation_date}
                    onChange={(v) => updateStageField(4, "facilitation_date", v)}
                    disabled={!isStageEditable(4)}
                  />
                  <FormField
                    label="Facilitator"
                    value={stageData.stage_4.facilitator}
                    onChange={(v) => updateStageField(4, "facilitator", v)}
                    disabled={!isStageEditable(4)}
                  />
                </div>
              </StageSection>

              <StageSection title="Cancellation" defaultOpen={false}>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <FormField
                    label="Cancel Date"
                    type="date"
                    value={stageData.stage_4.cancel_date}
                    onChange={(v) => updateStageField(4, "cancel_date", v)}
                    disabled={!isStageEditable(4)}
                  />
                  <FormField
                    label="Reason"
                    value={stageData.stage_4.cancel_reason}
                    onChange={(v) => updateStageField(4, "cancel_reason", v)}
                    disabled={!isStageEditable(4)}
                  />
                  <FormField
                    label="Category"
                    value={stageData.stage_4.cancel_category}
                    onChange={(v) => updateStageField(4, "cancel_category", v)}
                    disabled={!isStageEditable(4)}
                    options={DELAY_CATEGORIES}
                  />
                </div>
              </StageSection>

              <StageSection title="Redo" defaultOpen={false}>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <FormField
                    label="Success Date"
                    type="date"
                    value={stageData.stage_4.redo_success_date}
                    onChange={(v) => updateStageField(4, "redo_success_date", v)}
                    disabled={!isStageEditable(4)}
                  />
                  <FormField
                    label="Redo Qty"
                    type="number"
                    value={stageData.stage_4.redo_qty}
                    onChange={(v) => updateStageField(4, "redo_qty", parseInt(v))}
                    disabled={!isStageEditable(4)}
                  />
                  <FormField
                    label="Reason"
                    value={stageData.stage_4.redo_reason}
                    onChange={(v) => updateStageField(4, "redo_reason", v)}
                    disabled={!isStageEditable(4)}
                  />
                  <FormField
                    label="Category"
                    value={stageData.stage_4.redo_category}
                    onChange={(v) => updateStageField(4, "redo_category", v)}
                    disabled={!isStageEditable(4)}
                    options={REDO_CATEGORIES}
                  />
                </div>
              </StageSection>

              {isStageEditable(4) && (
                <div className="flex justify-end pt-4">
                  <Button onClick={() => saveStage(4)} disabled={saving} className="gap-2">
                    <Save className="w-4 h-4" />
                    {saving ? "Saving..." : "Save Stage 4"}
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Stage 5: Post SS */}
        <TabsContent value="stage_5">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-lg">Stage 5: Post SS</CardTitle>
                  <CardDescription>Post-Sleep Study analysis and follow-up</CardDescription>
                </div>
                {!isStageEditable(5) && <Lock className="w-4 h-4 text-muted-foreground" />}
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <StageSection title="Data & Analysis">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  <FormField
                    label="AHI Score"
                    type="number"
                    value={stageData.stage_5.ahi_score}
                    onChange={(v) => updateStageField(5, "ahi_score", parseFloat(v))}
                    disabled={!isStageEditable(5)}
                  />
                  <FormField
                    label="Data Contact Date"
                    type="date"
                    value={stageData.stage_5.data_contact_date}
                    onChange={(v) => updateStageField(5, "data_contact_date", v)}
                    disabled={!isStageEditable(5)}
                  />
                  <FormField
                    label="Responsible"
                    value={stageData.stage_5.data_responsible}
                    onChange={(v) => updateStageField(5, "data_responsible", v)}
                    disabled={!isStageEditable(5)}
                  />
                  <FormField
                    label="Lead Time"
                    type="number"
                    value={stageData.stage_5.data_lead_time}
                    onChange={(v) => updateStageField(5, "data_lead_time", parseInt(v))}
                    disabled={!isStageEditable(5)}
                  />
                  <FormField
                    label="Delay Reason"
                    value={stageData.stage_5.data_delay_reason}
                    onChange={(v) => updateStageField(5, "data_delay_reason", v)}
                    disabled={!isStageEditable(5)}
                  />
                  <FormField
                    label="Delay Category"
                    value={stageData.stage_5.data_delay_category}
                    onChange={(v) => updateStageField(5, "data_delay_category", v)}
                    disabled={!isStageEditable(5)}
                    options={DELAY_CATEGORIES}
                  />
                </div>
              </StageSection>

              <StageSection title="Pre-TT Contact" defaultOpen={false}>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <FormField
                    label="Contact 1"
                    type="date"
                    value={stageData.stage_5.pre_tt_contact_1}
                    onChange={(v) => updateStageField(5, "pre_tt_contact_1", v)}
                    disabled={!isStageEditable(5)}
                  />
                  <FormField
                    label="Contact 2"
                    type="date"
                    value={stageData.stage_5.pre_tt_contact_2}
                    onChange={(v) => updateStageField(5, "pre_tt_contact_2", v)}
                    disabled={!isStageEditable(5)}
                  />
                  <FormField
                    label="Contact 3"
                    type="date"
                    value={stageData.stage_5.pre_tt_contact_3}
                    onChange={(v) => updateStageField(5, "pre_tt_contact_3", v)}
                    disabled={!isStageEditable(5)}
                  />
                  <FormField
                    label="Lead Time"
                    type="number"
                    value={stageData.stage_5.pre_tt_lead_time}
                    onChange={(v) => updateStageField(5, "pre_tt_lead_time", parseInt(v))}
                    disabled={!isStageEditable(5)}
                  />
                </div>
              </StageSection>

              <StageSection title="TT Schedule" defaultOpen={false}>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  <FormField
                    label="TT Schedule Date"
                    type="date"
                    value={stageData.stage_5.tt_schedule_date}
                    onChange={(v) => updateStageField(5, "tt_schedule_date", v)}
                    disabled={!isStageEditable(5)}
                  />
                  <FormField
                    label="Responsible"
                    value={stageData.stage_5.tt_responsible}
                    onChange={(v) => updateStageField(5, "tt_responsible", v)}
                    disabled={!isStageEditable(5)}
                  />
                  <FormField
                    label="Lead Time"
                    type="number"
                    value={stageData.stage_5.tt_lead_time}
                    onChange={(v) => updateStageField(5, "tt_lead_time", parseInt(v))}
                    disabled={!isStageEditable(5)}
                  />
                </div>
              </StageSection>

              {isStageEditable(5) && (
                <div className="flex justify-end pt-4">
                  <Button onClick={() => saveStage(5)} disabled={saving} className="gap-2">
                    <Save className="w-4 h-4" />
                    {saving ? "Saving..." : "Save Stage 5"}
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Stage 6: SS Reports */}
        <TabsContent value="stage_6">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-lg">Stage 6: SS Reports</CardTitle>
                  <CardDescription>Sleep Study report generation and delivery</CardDescription>
                </div>
                {!isStageEditable(6) && <Lock className="w-4 h-4 text-muted-foreground" />}
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <StageSection title="Scoring">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  <FormField
                    label="Score Date"
                    type="date"
                    value={stageData.stage_6.score_date}
                    onChange={(v) => updateStageField(6, "score_date", v)}
                    disabled={!isStageEditable(6)}
                  />
                  <FormField
                    label="GRT Score"
                    value={stageData.stage_6.grt_score}
                    onChange={(v) => updateStageField(6, "grt_score", v)}
                    disabled={!isStageEditable(6)}
                  />
                  <FormField
                    label="Lead Time"
                    type="number"
                    value={stageData.stage_6.score_lead_time}
                    onChange={(v) => updateStageField(6, "score_lead_time", parseInt(v))}
                    disabled={!isStageEditable(6)}
                  />
                </div>
              </StageSection>

              <StageSection title="Send Report" defaultOpen={false}>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  <FormField
                    label="Send Date"
                    type="date"
                    value={stageData.stage_6.send_report_date}
                    onChange={(v) => updateStageField(6, "send_report_date", v)}
                    disabled={!isStageEditable(6)}
                  />
                  <FormField
                    label="Lead Time"
                    type="number"
                    value={stageData.stage_6.send_lead_time}
                    onChange={(v) => updateStageField(6, "send_lead_time", parseInt(v))}
                    disabled={!isStageEditable(6)}
                  />
                  <FormField
                    label="Delay Reason"
                    value={stageData.stage_6.send_delay_reason}
                    onChange={(v) => updateStageField(6, "send_delay_reason", v)}
                    disabled={!isStageEditable(6)}
                  />
                </div>
              </StageSection>

              {isStageEditable(6) && (
                <div className="flex justify-end pt-4">
                  <Button onClick={() => saveStage(6)} disabled={saving} className="gap-2">
                    <Save className="w-4 h-4" />
                    {saving ? "Saving..." : "Save Stage 6"}
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Stage 7: TT Facilitation (Commission Trigger) */}
        <TabsContent value="stage_7">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-lg flex items-center gap-2">
                    Stage 7: TT Facilitation
                    <Badge className="bg-emerald-100 text-emerald-800">
                      <DollarSign className="w-3 h-3 mr-1" />
                      Commission Trigger
                    </Badge>
                  </CardTitle>
                  <CardDescription>Titration Test execution - triggers commission eligibility</CardDescription>
                </div>
                {!isStageEditable(7) && <Lock className="w-4 h-4 text-muted-foreground" />}
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <StageSection title="Facilitation">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormField
                    label="Facilitation Date"
                    type="date"
                    value={stageData.stage_7.facilitation_date}
                    onChange={(v) => updateStageField(7, "facilitation_date", v)}
                    disabled={!isStageEditable(7)}
                  />
                  <FormField
                    label="Facilitator"
                    value={stageData.stage_7.facilitator}
                    onChange={(v) => updateStageField(7, "facilitator", v)}
                    disabled={!isStageEditable(7)}
                  />
                </div>
              </StageSection>

              <StageSection title="Redo" defaultOpen={false}>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <FormField
                    label="Redo Date"
                    type="date"
                    value={stageData.stage_7.redo_date}
                    onChange={(v) => updateStageField(7, "redo_date", v)}
                    disabled={!isStageEditable(7)}
                  />
                  <FormField
                    label="Redo Qty"
                    type="number"
                    value={stageData.stage_7.redo_qty}
                    onChange={(v) => updateStageField(7, "redo_qty", parseInt(v))}
                    disabled={!isStageEditable(7)}
                  />
                  <FormField
                    label="Reason"
                    value={stageData.stage_7.redo_reason}
                    onChange={(v) => updateStageField(7, "redo_reason", v)}
                    disabled={!isStageEditable(7)}
                  />
                  <FormField
                    label="Category"
                    value={stageData.stage_7.redo_category}
                    onChange={(v) => updateStageField(7, "redo_category", v)}
                    disabled={!isStageEditable(7)}
                    options={REDO_CATEGORIES}
                  />
                </div>
              </StageSection>

              <StageSection title="Cancellation" defaultOpen={false}>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <FormField
                    label="Cancel Date"
                    type="date"
                    value={stageData.stage_7.cancel_date}
                    onChange={(v) => updateStageField(7, "cancel_date", v)}
                    disabled={!isStageEditable(7)}
                  />
                  <FormField
                    label="Reason"
                    value={stageData.stage_7.cancel_reason}
                    onChange={(v) => updateStageField(7, "cancel_reason", v)}
                    disabled={!isStageEditable(7)}
                  />
                  <FormField
                    label="Category"
                    value={stageData.stage_7.cancel_category}
                    onChange={(v) => updateStageField(7, "cancel_category", v)}
                    disabled={!isStageEditable(7)}
                    options={DELAY_CATEGORIES}
                  />
                </div>
              </StageSection>

              {isStageEditable(7) && (
                <div className="flex justify-end pt-4">
                  <Button onClick={() => saveStage(7)} disabled={saving} className="gap-2">
                    <Save className="w-4 h-4" />
                    {saving ? "Saving..." : "Save Stage 7"}
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Stage 8: Post TT */}
        <TabsContent value="stage_8">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-lg">Stage 8: Post TT</CardTitle>
                  <CardDescription>Post-Titration Test processing</CardDescription>
                </div>
                {!isStageEditable(8) && <Lock className="w-4 h-4 text-muted-foreground" />}
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <StageSection title="Contact Patient">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  <FormField
                    label="TT Concluded Date"
                    type="date"
                    value={stageData.stage_8.tt_concluded_date}
                    onChange={(v) => updateStageField(8, "tt_concluded_date", v)}
                    disabled={!isStageEditable(8)}
                  />
                  <FormField
                    label="Responsible"
                    value={stageData.stage_8.contact_responsible}
                    onChange={(v) => updateStageField(8, "contact_responsible", v)}
                    disabled={!isStageEditable(8)}
                  />
                  <FormField
                    label="Lead Time"
                    type="number"
                    value={stageData.stage_8.contact_lead_time}
                    onChange={(v) => updateStageField(8, "contact_lead_time", parseInt(v))}
                    disabled={!isStageEditable(8)}
                  />
                </div>
              </StageSection>

              <StageSection title="Load Data" defaultOpen={false}>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  <FormField
                    label="Load Date"
                    type="date"
                    value={stageData.stage_8.load_data_date}
                    onChange={(v) => updateStageField(8, "load_data_date", v)}
                    disabled={!isStageEditable(8)}
                  />
                  <FormField
                    label="Responsible"
                    value={stageData.stage_8.load_responsible}
                    onChange={(v) => updateStageField(8, "load_responsible", v)}
                    disabled={!isStageEditable(8)}
                  />
                  <FormField
                    label="Lead Time"
                    type="number"
                    value={stageData.stage_8.load_lead_time}
                    onChange={(v) => updateStageField(8, "load_lead_time", parseInt(v))}
                    disabled={!isStageEditable(8)}
                  />
                </div>
              </StageSection>

              {isStageEditable(8) && (
                <div className="flex justify-end pt-4">
                  <Button onClick={() => saveStage(8)} disabled={saving} className="gap-2">
                    <Save className="w-4 h-4" />
                    {saving ? "Saving..." : "Save Stage 8"}
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Stage 9: TT Sum */}
        <TabsContent value="stage_9">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-lg">Stage 9: TT Sum</CardTitle>
                  <CardDescription>Titration Test data summarization</CardDescription>
                </div>
                {!isStageEditable(9) && <Lock className="w-4 h-4 text-muted-foreground" />}
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <FormField
                  label="Summarize Date"
                  type="date"
                  value={stageData.stage_9.summarize_date}
                  onChange={(v) => updateStageField(9, "summarize_date", v)}
                  disabled={!isStageEditable(9)}
                />
                <FormField
                  label="Lead Time"
                  type="number"
                  value={stageData.stage_9.summarize_lead_time}
                  onChange={(v) => updateStageField(9, "summarize_lead_time", parseInt(v))}
                  disabled={!isStageEditable(9)}
                />
                <FormField
                  label="Delay Reason"
                  value={stageData.stage_9.summarize_delay_reason}
                  onChange={(v) => updateStageField(9, "summarize_delay_reason", v)}
                  disabled={!isStageEditable(9)}
                />
                <FormField
                  label="Delay Category"
                  value={stageData.stage_9.summarize_delay_category}
                  onChange={(v) => updateStageField(9, "summarize_delay_category", v)}
                  disabled={!isStageEditable(9)}
                  options={DELAY_CATEGORIES}
                />
              </div>

              {isStageEditable(9) && (
                <div className="flex justify-end pt-4">
                  <Button onClick={() => saveStage(9)} disabled={saving} className="gap-2">
                    <Save className="w-4 h-4" />
                    {saving ? "Saving..." : "Save Stage 9"}
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Stage 10: TT Report (Final) */}
        <TabsContent value="stage_10">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-lg flex items-center gap-2">
                    Stage 10: TT Report
                    <Badge variant="secondary">Final Stage</Badge>
                  </CardTitle>
                  <CardDescription>Final reporting, patient feedback, and referral</CardDescription>
                </div>
                {!isStageEditable(10) && <Lock className="w-4 h-4 text-muted-foreground" />}
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <ScrollArea className="h-[500px] pr-4">
                <StageSection title="GRT Scoring">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <FormField
                      label="GRT Date"
                      type="date"
                      value={stageData.stage_10.grt_date}
                      onChange={(v) => updateStageField(10, "grt_date", v)}
                      disabled={!isStageEditable(10)}
                    />
                    <FormField
                      label="Lead Time"
                      type="number"
                      value={stageData.stage_10.grt_lead_time}
                      onChange={(v) => updateStageField(10, "grt_lead_time", parseInt(v))}
                      disabled={!isStageEditable(10)}
                    />
                    <FormField
                      label="Delay Reason"
                      value={stageData.stage_10.grt_delay_reason}
                      onChange={(v) => updateStageField(10, "grt_delay_reason", v)}
                      disabled={!isStageEditable(10)}
                    />
                    <FormField
                      label="Delay Category"
                      value={stageData.stage_10.grt_delay_category}
                      onChange={(v) => updateStageField(10, "grt_delay_category", v)}
                      disabled={!isStageEditable(10)}
                      options={DELAY_CATEGORIES}
                    />
                  </div>
                </StageSection>

                <StageSection title="Send TT Report" defaultOpen={false}>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    <FormField
                      label="Send Date"
                      type="date"
                      value={stageData.stage_10.send_report_date}
                      onChange={(v) => updateStageField(10, "send_report_date", v)}
                      disabled={!isStageEditable(10)}
                    />
                    <FormField
                      label="Lead Time"
                      type="number"
                      value={stageData.stage_10.send_lead_time}
                      onChange={(v) => updateStageField(10, "send_lead_time", parseInt(v))}
                      disabled={!isStageEditable(10)}
                    />
                    <FormField
                      label="Delay Reason"
                      value={stageData.stage_10.send_delay_reason}
                      onChange={(v) => updateStageField(10, "send_delay_reason", v)}
                      disabled={!isStageEditable(10)}
                    />
                  </div>
                </StageSection>

                <StageSection title="Patient Feedback" defaultOpen={false}>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <FormField
                      label="Contact 1"
                      type="date"
                      value={stageData.stage_10.feedback_contact_1}
                      onChange={(v) => updateStageField(10, "feedback_contact_1", v)}
                      disabled={!isStageEditable(10)}
                    />
                    <FormField
                      label="Contact 2"
                      type="date"
                      value={stageData.stage_10.feedback_contact_2}
                      onChange={(v) => updateStageField(10, "feedback_contact_2", v)}
                      disabled={!isStageEditable(10)}
                    />
                    <FormField
                      label="Contact 3"
                      type="date"
                      value={stageData.stage_10.feedback_contact_3}
                      onChange={(v) => updateStageField(10, "feedback_contact_3", v)}
                      disabled={!isStageEditable(10)}
                    />
                    <FormField
                      label="Contact 4"
                      type="date"
                      value={stageData.stage_10.feedback_contact_4}
                      onChange={(v) => updateStageField(10, "feedback_contact_4", v)}
                      disabled={!isStageEditable(10)}
                    />
                    <FormField
                      label="Feedback Given Date"
                      type="date"
                      value={stageData.stage_10.feedback_given_date}
                      onChange={(v) => updateStageField(10, "feedback_given_date", v)}
                      disabled={!isStageEditable(10)}
                    />
                    <FormField
                      label="Responsible"
                      value={stageData.stage_10.feedback_responsible}
                      onChange={(v) => updateStageField(10, "feedback_responsible", v)}
                      disabled={!isStageEditable(10)}
                    />
                  </div>
                  <div className="mt-4">
                    <Label className="text-xs text-muted-foreground">Feedback Text</Label>
                    <textarea
                      className={cn(
                        "w-full mt-1 p-2 border rounded-md text-sm min-h-[80px]",
                        !isStageEditable(10) && "bg-muted"
                      )}
                      value={stageData.stage_10.feedback_text || ""}
                      onChange={(e) => updateStageField(10, "feedback_text", e.target.value)}
                      disabled={!isStageEditable(10)}
                      placeholder="Enter patient feedback..."
                    />
                  </div>
                </StageSection>

                <StageSection title="Patient Referral" defaultOpen={false}>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <FormField
                      label="Referral Date"
                      type="date"
                      value={stageData.stage_10.referral_date}
                      onChange={(v) => updateStageField(10, "referral_date", v)}
                      disabled={!isStageEditable(10)}
                    />
                    <FormField
                      label="Referred To"
                      value={stageData.stage_10.referred_to}
                      onChange={(v) => updateStageField(10, "referred_to", v)}
                      disabled={!isStageEditable(10)}
                    />
                    <div className="flex items-center gap-3 pt-6">
                      <Switch
                        checked={stageData.stage_10.referral_contacted || false}
                        onCheckedChange={(v) => updateStageField(10, "referral_contacted", v)}
                        disabled={!isStageEditable(10)}
                      />
                      <Label className="text-sm">Contacted</Label>
                    </div>
                  </div>
                </StageSection>

                <StageSection title="Cancellation" defaultOpen={false}>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <FormField
                      label="Cancel Date"
                      type="date"
                      value={stageData.stage_10.cancel_date}
                      onChange={(v) => updateStageField(10, "cancel_date", v)}
                      disabled={!isStageEditable(10)}
                    />
                    <FormField
                      label="Reason"
                      value={stageData.stage_10.cancel_reason}
                      onChange={(v) => updateStageField(10, "cancel_reason", v)}
                      disabled={!isStageEditable(10)}
                    />
                    <FormField
                      label="Category"
                      value={stageData.stage_10.cancel_category}
                      onChange={(v) => updateStageField(10, "cancel_category", v)}
                      disabled={!isStageEditable(10)}
                      options={DELAY_CATEGORIES}
                    />
                  </div>
                </StageSection>
              </ScrollArea>

              {isStageEditable(10) && (
                <div className="flex justify-end pt-4">
                  <Button onClick={() => saveStage(10)} disabled={saving} className="gap-2">
                    <Save className="w-4 h-4" />
                    {saving ? "Saving..." : "Save Stage 10"}
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default CaseDetailPage;
